import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, MailCheck } from "lucide-react";
import { memberApi } from "../api/member";

const initialPasswordForm = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const initialEmailForm = {
  currentPassword: "",
  newEmail: "",
};

export default function AccountSecurityForms({ loginPath }) {
  const navigate = useNavigate();
  const [passwordForm, setPasswordForm] = useState(initialPasswordForm);
  const [emailForm, setEmailForm] = useState(initialEmailForm);
  const [passwordStatus, setPasswordStatus] = useState("idle");
  const [emailStatus, setEmailStatus] = useState("idle");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [emailMessage, setEmailMessage] = useState("");

  function updatePassword(field, value) {
    setPasswordForm((current) => ({ ...current, [field]: value }));
  }

  function updateEmail(field, value) {
    setEmailForm((current) => ({ ...current, [field]: value }));
  }

  async function submitPassword(event) {
    event.preventDefault();
    setPasswordStatus("loading");
    setPasswordMessage("");
    try {
      const payload = await memberApi("/api/auth/change-password", {
        method: "POST",
        body: JSON.stringify(passwordForm),
      });
      setPasswordStatus("success");
      setPasswordMessage(payload.message || "Password changed successfully.");
      setPasswordForm(initialPasswordForm);
      window.setTimeout(() => navigate(loginPath, { replace: true }), 1200);
    } catch (error) {
      setPasswordStatus("error");
      setPasswordMessage(
        error instanceof Error ? error.message : "Unable to change password."
      );
    }
  }

  async function submitEmailChange(event) {
    event.preventDefault();
    setEmailStatus("loading");
    setEmailMessage("");
    try {
      const payload = await memberApi("/api/auth/request-email-change", {
        method: "POST",
        body: JSON.stringify(emailForm),
      });
      setEmailStatus("success");
      setEmailMessage(
        payload.message || "We sent a verification link to your new email address."
      );
      setEmailForm(initialEmailForm);
    } catch (error) {
      setEmailStatus("error");
      setEmailMessage(
        error instanceof Error ? error.message : "Unable to request email change."
      );
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <form
        onSubmit={submitPassword}
        className="rounded-2xl border border-[#C9A227]/20 bg-white p-6"
      >
        <div className="flex items-center gap-3">
          <KeyRound className="h-5 w-5 text-[#722F37]" />
          <h2 className="font-serif-elegant text-2xl font-bold text-[#722F37]">
            Change password
          </h2>
        </div>

        {passwordMessage && (
          <div
            className={`mt-5 rounded-xl border px-4 py-3 text-sm ${
              passwordStatus === "success"
                ? "border-green-200 bg-green-50 text-green-900"
                : "border-red-200 bg-red-50 text-red-900"
            }`}
          >
            {passwordMessage}
          </div>
        )}

        <div className="mt-6 space-y-4">
          <PasswordInput
            label="Current password"
            value={passwordForm.currentPassword}
            autoComplete="current-password"
            onChange={(value) => updatePassword("currentPassword", value)}
          />
          <PasswordInput
            label="New password"
            value={passwordForm.newPassword}
            autoComplete="new-password"
            onChange={(value) => updatePassword("newPassword", value)}
          />
          <PasswordInput
            label="Confirm new password"
            value={passwordForm.confirmPassword}
            autoComplete="new-password"
            onChange={(value) => updatePassword("confirmPassword", value)}
          />
        </div>

        <button
          type="submit"
          disabled={passwordStatus === "loading"}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#722F37] px-5 py-3 font-semibold text-white hover:bg-[#5A252C] disabled:opacity-70"
        >
          {passwordStatus === "loading" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <KeyRound className="h-4 w-4" />
          )}
          Update password
        </button>
      </form>

      <form
        onSubmit={submitEmailChange}
        className="rounded-2xl border border-[#C9A227]/20 bg-white p-6"
      >
        <div className="flex items-center gap-3">
          <MailCheck className="h-5 w-5 text-[#722F37]" />
          <h2 className="font-serif-elegant text-2xl font-bold text-[#722F37]">
            Change email
          </h2>
        </div>

        {emailMessage && (
          <div
            className={`mt-5 rounded-xl border px-4 py-3 text-sm ${
              emailStatus === "success"
                ? "border-green-200 bg-green-50 text-green-900"
                : "border-red-200 bg-red-50 text-red-900"
            }`}
          >
            {emailMessage}
          </div>
        )}

        <div className="mt-6 space-y-4">
          <PasswordInput
            label="Current password"
            value={emailForm.currentPassword}
            autoComplete="current-password"
            onChange={(value) => updateEmail("currentPassword", value)}
          />
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">
              New email
            </span>
            <input
              type="email"
              value={emailForm.newEmail}
              onChange={(event) => updateEmail("newEmail", event.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              autoComplete="email"
              required
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={emailStatus === "loading"}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#722F37] px-5 py-3 font-semibold text-white hover:bg-[#5A252C] disabled:opacity-70"
        >
          {emailStatus === "loading" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MailCheck className="h-4 w-4" />
          )}
          Send verification link
        </button>
      </form>
    </div>
  );
}

function PasswordInput({ label, value, autoComplete, onChange }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-gray-700">
        {label}
      </span>
      <input
        type="password"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
        autoComplete={autoComplete}
        required
      />
    </label>
  );
}
